/**
 * Настройки проекта.
 *
 * Опасные операции вынесены в отдельную зону и требуют подтверждения названием
 * проекта (документ «Структура интерфейса», §18).
 */

import { zodResolver } from '@hookform/resolvers/zod';
import { ImageUpload } from '@/components/common/image-upload';
import { ProjectLogo } from '@/components/common/project-logo';
import { ArchiveIcon, DownloadIcon, FileTextIcon, Loader2Icon, SaveIcon } from 'lucide-react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router';
import { toast } from 'sonner';
import type { ProjectDto } from '@project-os/contracts';
import { PROJECT_STATUSES, PROJECT_STATUS_LABELS_RU } from '@project-os/contracts';
import { updateProjectSchema } from '@project-os/validation';
import { ApiError } from '@/api/client';
import { useArchiveProject, useUpdateProject } from '@/api/projects';
import { PageBody, PageHeader } from '@/components/layout/PageHeader';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { exportUrl } from '@/api/search';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { useProjectMembers } from '@/api/members';
import { useClearProjectLogo, useUploadProjectLogo } from '@/api/projects';
import { useProjectContext } from './use-project-context';
import { WorkstreamsCard } from './WorkstreamsCard';

function ArchiveDialog({ projectId, projectName }: { projectId: string; projectName: string }) {
  const [open, setOpen] = useState(false);
  const [confirmation, setConfirmation] = useState('');
  const navigate = useNavigate();
  const archive = useArchiveProject(projectId);

  const handleArchive = async () => {
    try {
      await archive.mutateAsync();
      toast.success('Проект архивирован');
      void navigate('/projects', { replace: true });
    } catch (error) {
      toast.error(error instanceof ApiError ? error.message : 'Не удалось архивировать проект');
    }
  };

  return (
    <>
      <Button variant="destructive" size="sm" onClick={() => setOpen(true)}>
        <ArchiveIcon />
        Архивировать проект
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Архивировать проект</DialogTitle>
            <DialogDescription>
              Проект и все его данные сохранятся, но исчезнут из обычных списков. Ничего не
              удаляется: архивирование можно отменить.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-2">
            <Label htmlFor="archive-confirm">
              Для подтверждения введите название проекта: <b>{projectName}</b>
            </Label>
            <Input
              id="archive-confirm"
              value={confirmation}
              onChange={(event) => setConfirmation(event.target.value)}
              autoComplete="off"
            />
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>
              Отмена
            </Button>
            <Button
              variant="destructive"
              disabled={confirmation !== projectName || archive.isPending}
              onClick={() => void handleArchive()}
            >
              {archive.isPending ? <Loader2Icon className="animate-spin" /> : null}
              Архивировать
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

export function SettingsPage() {
  const project = useProjectContext();
  const updateProject = useUpdateProject(project.id);

  const { data: members = [] } = useProjectMembers(project.id);
  const canEdit = project.permissions.includes('ITEM_WRITE');
  const canArchive = project.permissions.includes('PROJECT_ARCHIVE');

  const form = useForm({
    resolver: zodResolver(updateProjectSchema),
    defaultValues: {
      name: project.name,
      descriptionMd: project.descriptionMd ?? '',
      status: project.status,
      currentGoal: project.currentGoal ?? '',
      startDate: project.startDate,
      targetDate: project.targetDate,
    },
  });

  const onSubmit = form.handleSubmit(async (values) => {
    try {
      await updateProject.mutateAsync({
        ...values,
        descriptionMd: values.descriptionMd || null,
        currentGoal: values.currentGoal || null,
        startDate: values.startDate || null,
        targetDate: values.targetDate || null,
      });
      toast.success('Изменения сохранены');
      form.reset(values);
    } catch (error) {
      if (error instanceof ApiError && error.fields) {
        for (const [field, message] of Object.entries(error.fields)) {
          form.setError(field as Parameters<typeof form.setError>[0], { message });
        }
      } else {
        toast.error(error instanceof ApiError ? error.message : 'Не удалось сохранить');
      }
    }
  });

  return (
    <>
      <PageHeader title="Настройки" description="Основные сведения о проекте" />

      <PageBody className="max-w-3xl space-y-5">
        {!canEdit ? (
          <Alert>
            <AlertDescription>
              Ваша роль в проекте не позволяет менять настройки. Поля показаны только для чтения.
            </AlertDescription>
          </Alert>
        ) : null}

        <Card>
          <CardHeader>
            <CardTitle className="text-sm">Общие</CardTitle>
          </CardHeader>
          <CardContent>
            <Form {...form}>
              <form onSubmit={onSubmit} className="space-y-4" noValidate>
                <FormField
                  control={form.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Название</FormLabel>
                      <FormControl>
                        <Input disabled={!canEdit} {...field} value={field.value ?? ''} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="currentGoal"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Текущая цель</FormLabel>
                      <FormControl>
                        <Textarea
                          rows={2}
                          disabled={!canEdit}
                          {...field}
                          value={field.value ?? ''}
                        />
                      </FormControl>
                      <FormDescription>
                        Чего команда должна добиться прямо сейчас. Показывается на обзоре и в списке
                        проектов.
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="descriptionMd"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Описание</FormLabel>
                      <FormControl>
                        <Textarea
                          rows={4}
                          disabled={!canEdit}
                          {...field}
                          value={field.value ?? ''}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <div className="grid gap-4 sm:grid-cols-3">
                  <FormField
                    control={form.control}
                    name="status"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Состояние</FormLabel>
                        <Select
                          value={field.value}
                          onValueChange={field.onChange}
                          disabled={!canEdit}
                        >
                          <FormControl>
                            <SelectTrigger className="w-full">
                              <SelectValue />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {PROJECT_STATUSES.map((status) => (
                              <SelectItem key={status} value={status}>
                                {PROJECT_STATUS_LABELS_RU[status]}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="startDate"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Дата начала</FormLabel>
                        <FormControl>
                          <Input
                            type="date"
                            disabled={!canEdit}
                            {...field}
                            value={field.value ?? ''}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="targetDate"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Целевая дата</FormLabel>
                        <FormControl>
                          <Input
                            type="date"
                            disabled={!canEdit}
                            {...field}
                            value={field.value ?? ''}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                {canEdit ? (
                  <div className="flex justify-end">
                    <Button
                      type="submit"
                      disabled={updateProject.isPending || !form.formState.isDirty}
                    >
                      {updateProject.isPending ? (
                        <Loader2Icon className="animate-spin" />
                      ) : (
                        <SaveIcon />
                      )}
                      Сохранить
                    </Button>
                  </div>
                ) : null}
              </form>
            </Form>
          </CardContent>
        </Card>

        <WorkstreamsCard
          projectId={project.id}
          members={members.map((member) => member.user)}
          canEdit={canEdit}
        />

        <LogoCard project={project} canEdit={canEdit} />

        <ExportCard projectId={project.id} projectName={project.name} />

        {canArchive ? (
          <Card className="border-destructive/40">
            <CardHeader>
              <CardTitle className="text-sm text-destructive">Опасная зона</CardTitle>
            </CardHeader>
            <CardContent className="flex flex-wrap items-center justify-between gap-3">
              <p className="max-w-md text-xs leading-relaxed text-muted-foreground">
                Архивирование убирает проект из рабочих списков. Данные, история и файлы сохраняются
                — платформа не удаляет проектную информацию.
              </p>
              <ArchiveDialog projectId={project.id} projectName={project.name} />
            </CardContent>
          </Card>
        ) : null}
      </PageBody>
    </>
  );
}

// --- Выгрузка ------------------------------------------------------------

/**
 * Экспорт проекта.
 *
 * Смысл выгрузки — независимость от приложения: она должна читаться, когда
 * самого Project OS уже нет. Скачивание идёт обычной ссылкой, а не запросом:
 * браузер сам сохранит файл и не будет держать выгрузку в памяти вкладки.
 */
function ExportCard({ projectId, projectName }: { projectId: string; projectName: string }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-sm">Выгрузка</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="max-w-2xl text-xs leading-relaxed text-muted-foreground">
          Выгрузка содержит проект целиком: элементы, связи, этапы, обсуждение, проверки, версии и
          список файлов с контрольными суммами. Паролей, ключей и сессий в ней нет — этих данных нет
          и в самой системе. Содержимое файлов скачивается отдельно по ссылкам из манифеста.
        </p>

        <div className="flex flex-wrap gap-2">
          <Button variant="outline" size="sm" asChild>
            <a href={exportUrl(projectId, 'json')} download>
              <DownloadIcon />
              JSON
            </a>
          </Button>
          <Button variant="outline" size="sm" asChild>
            <a href={exportUrl(projectId, 'markdown')} download>
              <FileTextIcon />
              Markdown
            </a>
          </Button>
        </div>

        <p className="text-2xs leading-relaxed text-muted-foreground/80">
          JSON читается программой, Markdown — человеком. Каждая выгрузка «{projectName}»
          записывается в журнал проекта.
        </p>
      </CardContent>
    </Card>
  );
}

// --- Логотип проекта -------------------------------------------------------

/**
 * Логотип проекта.
 *
 * Виден в списке проектов, в сайдбаре и в переключателе — то есть везде, где
 * проект нужно узнать, не читая названия. Пока логотипа нет, там же рисуются
 * первые буквы названия.
 */
function LogoCard({ project, canEdit }: { project: ProjectDto; canEdit: boolean }) {
  const upload = useUploadProjectLogo(project.id);
  const clear = useClearProjectLogo(project.id);

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm">Логотип</CardTitle>
      </CardHeader>
      <CardContent>
        <ImageUpload
          label="Загрузить логотип"
          hint="Виден в списке проектов, в боковой панели и в переключателе. PNG, JPG, SVG или WebP, до 5 МБ."
          canClear={Boolean(project.logoUrl)}
          disabled={!canEdit}
          preview={<ProjectLogo project={project} size="xl" className="size-16" />}
          onUpload={(file) => upload.mutateAsync(file)}
          onClear={() => clear.mutateAsync()}
        />
      </CardContent>
    </Card>
  );
}
